// services/storeService.ts
import storeJson from "@/public/ecomsaas-assets/cache/stores.json";

// Safe store retrieval with fallback
const store =
  Array.isArray(storeJson) && storeJson.length > 0 ? storeJson[0] : null;

console.log(store, "store");

/* =========================================================
   LOGO FUNCTIONS
   ========================================================= */
export const getLogo = (): string => {
  if (!store || !process.env.NEXT_PUBLIC_STORE_CODE) {
    return "https://blr1.digitaloceanspaces.com/assetsvilva/vilvabusiness/website/common/otherallimages/empty-logowebp.webp";
  }
  return `https://assetsvilva.blr1.cdn.digitaloceanspaces.com/ecomsaas/redisapi/${process.env.NEXT_PUBLIC_STORE_CODE}/logos/logowebp.webp`;
};

export const getDarkLogo = (): string => {
  return getLogo(); // Reuse same logic
};

export const getLightLogo = (): string => {
  return getLogo(); // Reuse same logic
};

/* =========================================================
   META INFORMATION
   ========================================================= */
export const getMetaTitle = (): string => {
  if (!store) return "Online Shopping Store";
  return store.title || store.store || "Online Shopping Store";
};

export const getMetaDescription = (): string => {
  if (!store)
    return "Shop quality products online with best prices and fast delivery";
  return (
    store.description ||
    `Shop at ${store.store || "our store"}` ||
    "Quality products online"
  );
};

export const getMetaImageURL = (): string => {
  if (!process.env.NEXT_PUBLIC_STORE_CODE) {
    return "https://assetsvilva.blr1.cdn.digitaloceanspaces.com/ecomsaas/redisapi/default/logos/logowebp.webp";
  }
  return `https://assetsvilva.blr1.cdn.digitaloceanspaces.com/ecomsaas/redisapi/${process.env.NEXT_PUBLIC_STORE_CODE}/logos/logowebp.webp`;
};

/* =========================================================
   STORE INFORMATION
   ========================================================= */
export const getStoreName = (): string => {
  if (!store) return process.env.NEXT_PUBLIC_STORE_NAME || "My Store";
  return store.store || process.env.NEXT_PUBLIC_STORE_NAME || "My Store";
};

export const getStoreTitle = (): string => {
  if (!store) return "Your Store Title";
  return store.title || store.store || "Your Store Title";
};

export const getStoreDescription = (): string => {
  if (!store) return "Your online shopping destination";
  return store.description || "Your online shopping destination";
};

export const getLegalEntity = (): string => {
  if (!store) return "";
  return store.store || "";
};

/* =========================================================
   KEYWORDS - FIXED WITH PROPER FALLBACK
   ========================================================= */
export const getStoreKeywords = (): string[] => {
  try {
    if (!store) {
      return ["ecommerce", "online shopping", "shop online"];
    }

    // Check if keywords exist in store data
    const storeWithKeywords = store as any;

    // If keywords is a string (comma-separated)
    if (
      typeof storeWithKeywords.keywords === "string" &&
      storeWithKeywords.keywords.trim()
    ) {
      return storeWithKeywords.keywords
        .split(",")
        .map((kw: string) => kw.trim())
        .filter((kw: string) => kw.length > 0);
    }

    // If keywords is already an array
    if (
      Array.isArray(storeWithKeywords.keywords) &&
      storeWithKeywords.keywords.length > 0
    ) {
      return storeWithKeywords.keywords;
    }

    // Fallback keywords
    return [
      store.store || "online store",
      "ecommerce",
      "online shopping",
      "shop online",
    ];
  } catch (error) {
    console.error("Error getting keywords:", error);
    return ["ecommerce", "online shopping"];
  }
};

/* =========================================================
   URLs
   ========================================================= */
export const getBaseUrl = (): string => {
  if (!store || !store.storeurl) {
    return "http://localhost:3000/";
  }
  let url = store.storeurl
    .replace(/\\\//g, "/") // Convert \/ to /
    .replace(/\/+$/, ""); // Remove trailing slashes

  return url;
};

export const getStoreUrl = (): string => {
  if (!store || !store.location_url) {
    return "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3885.289360495459!2d80.24638477578391!3d13.144138987187317!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a526511af9e5353%3A0x196d22c36e1f9c72!2sVilva%20Networks!5e0!3m2!1sen!2sin!4v1765614997870!5m2!1sen!2sin";
  }
  return store.location_url;
};

/* =========================================================
   ADDRESS & LOCATION
   ========================================================= */
// export const getStoreAddress = (): string => {
//   if (!store || !store.address) {
//     return "Your Store Address";
//   }
//   return store.address.replace(/\n/g, "<br/>");
// };

export const getStoreCountryCode = (): string => {
  try {
    const region = getRegion();
    return region?.country_code || "IN";
  } catch {
    return "IN";
  }
};

export const getStoreCountry = (): string => {
  try {
    const region = getRegion();
    return region?.country || "India";
  } catch {
    return "India";
  }
};

/* =========================================================
   CONTACT INFORMATION
   ========================================================= */
export const getSupportMobile = (): string => {
  if (!store) return "";
  return store.support_mobile || "";
};

export const getSupportEmail = (): string => {
  if (!store) return "";
  return store.support_email || "";
};

export const getCurrencySymbol = (): string => {
  if (!store) return "₹";
  return store.currency_symbol || "₹";
};

/* =========================================================
   SOCIAL LINKS
   ========================================================= */
export const getSocialLinks = (): any[] => {
  try {
    if (!store || !store.social) return [];
    return typeof store.social === "string"
      ? JSON.parse(store.social)
      : store.social;
  } catch (error) {
    console.error("Error parsing social links:", error);
    return [];
  }
};

export const getSocialLinkByName = (name: string): string | null => {
  try {
    const socials = getSocialLinks();
    if (!socials || socials.length === 0) return null;

    const match = socials.find((s: any) =>
      s?.name?.toLowerCase()?.includes(name.toLowerCase())
    );

    return match?.value || null;
  } catch (error) {
    console.error(`Error getting social link for ${name}:`, error);
    return null;
  }
};

/* =========================================================
   REGION & CONFIGURATIONS
   ========================================================= */
export const getRegion = (): any => {
  try {
    if (!store || !store.region) return {};
    return typeof store.region === "string"
      ? JSON.parse(store.region)
      : store.region;
  } catch (error) {
    console.error("Error parsing region:", error);
    return {};
  }
};

export const getGatewayConfig = (): any[] => {
  try {
    if (!store || !store.gateway_config) return [];
    return typeof store.gateway_config === "string"
      ? JSON.parse(store.gateway_config)
      : store.gateway_config;
  } catch (error) {
    console.error("Error parsing gateway config:", error);
    return [];
  }
};

export const getMarketingConfig = (): any[] => {
  try {
    if (!store || !store.marketing_config) return [];
    return typeof store.marketing_config === "string"
      ? JSON.parse(store.marketing_config)
      : store.marketing_config;
  } catch (error) {
    console.error("Error parsing marketing config:", error);
    return [];
  }
};

export const getPartialCOD = (): any => {
  try {
    if (!store || !store.partial_cod) return {};
    return typeof store.partial_cod === "string"
      ? JSON.parse(store.partial_cod)
      : store.partial_cod;
  } catch (error) {
    console.error("Error parsing partial COD:", error);
    return {};
  }
};

/* =========================================================
   MARKETING IDs
   ========================================================= */
export const getFacebookDatasetId = (): string | null => {
  try {
    const configs = getMarketingConfig();
    if (!Array.isArray(configs) || configs.length === 0) return null;

    const fbConfig = configs.find((c: any) =>
      c?.name?.toLowerCase()?.includes("facebook conversion")
    );
    return fbConfig?.datasetid || null;
  } catch (error) {
    console.error("Error getting Facebook dataset ID:", error);
    return null;
  }
};

export const getGoogleAnalyticsId = (): string | null => {
  try {
    const configs = getMarketingConfig();
    if (!Array.isArray(configs) || configs.length === 0) return null;

    const gaConfig = configs.find((c: any) =>
      c?.name?.toLowerCase()?.includes("google analytics")
    );
    return gaConfig?.measurement_id || null;
  } catch (error) {
    console.error("Error getting Google Analytics ID:", error);
    return null;
  }
};

/* =========================================================
   COUPONS & ASSETS
   ========================================================= */
export const getCoupons = (): any[] => {
  if (!store) return [];
  return Array.isArray(store.coupons) ? store.coupons : [];
};

export const getAssetsBaseUrl = (): string => {
  if (!store || !store.assets) {
    return `https://assetsvilva.blr1.cdn.digitaloceanspaces.com/ecomsaas/redisapi/${
      process.env.NEXT_PUBLIC_STORE_CODE || "default"
    }/`;
  }
  return store.assets;
};
